import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSidenav } from '@angular/material/sidenav';
import { ActivatedRoute, Router } from '@angular/router';
import { BlockUI, NgBlockUI } from 'ng-block-ui';
import { firstValueFrom } from 'rxjs';
import { AuthUser } from '../../models/auth-user.model';
import { ApiService } from '../../services/api.service';
import { AuthService } from '../../services/auth.service';
import { NotificationsService } from '../../services/notifications.service';
import { SidenavService } from '../../services/sidenav.service';
import { SnackbarService } from '../../services/snackbar.service';
import { UsersService } from '../../services/users.service';
import { ConfirmModalComponent } from '../shared/confirm-modal/confirm-modal.component';

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
  standalone: false,
})
export class ProfileComponent implements OnInit {
  @BlockUI() blockUI: NgBlockUI;

  sidenav: MatSidenav;
  user: any;
  userId: string = '';
  isAdminView = false;
  pageTitle = 'Perfil';
  currentPassword = '';
  newPassword = '';
  confirmPassword = '';
  hideCurrentPassword = true;
  hideNewPassword = true;
  hideConfirmPassword = true;
  notificationsSupported = false;
  notificationsEnabled = false;

  constructor(
    private dialog: MatDialog,
    private sidenavService: SidenavService,
    private usersService: UsersService,
    private route: ActivatedRoute,
    private snackbar: SnackbarService,
    private router: Router,
    private authService: AuthService,
    private apiService: ApiService,
    private notificationsService: NotificationsService,
    private cdr: ChangeDetectorRef,
  ) {
    this.sidenav = this.sidenavService.getSidenav();
    this.user = {};
  }

  async ngOnInit(): Promise<void> {
    const routeUserId = this.route.snapshot.params['id'];
    this.isAdminView =
      this.route.snapshot.routeConfig?.path === 'admin/users/:id';

    if (routeUserId) {
      this.userId = routeUserId;
    } else {
      const loggedUser = await this.authService.getUserAsync();

      if (!loggedUser?._id) {
        this.router.navigate(['/login']);
        return;
      }

      this.userId = loggedUser._id;
    }

    this.user = await this.usersService.get(this.userId);
    this.pageTitle = this.isAdminView
      ? this.user?.name || this.user?.email || 'Usuario'
      : 'Perfil';

    if (!this.isAdminView) {
      this.notificationsSupported = this.notificationsService.isSupported();
      this.notificationsEnabled = await this.notificationsService.isEnabled();
      this.cdr.markForCheck();
    }
  }

  toggleMenu(): void {
    this.sidenavService.toggle();
  }

  goBack(): void {
    this.router.navigate(['/admin/users']);
  }

  update(): void {
    const newPassword = this.newPassword.trim();
    const currentPassword = this.currentPassword.trim();
    const confirmPassword = this.confirmPassword.trim();

    if (!this.isAdminView && currentPassword && !newPassword) {
      this.snackbar.error(
        'Introduce una nueva contraseña o deja ambos campos vacíos.',
      );
      return;
    }

    if (!this.isAdminView && newPassword && !currentPassword) {
      this.snackbar.error(
        'Para cambiar la contraseña, primero introduce tu contraseña actual.',
      );
      return;
    }

    if (newPassword && !confirmPassword) {
      this.snackbar.error('Confirma la nueva contraseña para continuar.');
      return;
    }

    if (!newPassword && confirmPassword) {
      this.snackbar.error(
        'Introduce una nueva contraseña o deja ambos campos vacíos.',
      );
      return;
    }

    if (newPassword && confirmPassword !== newPassword) {
      this.snackbar.error('La confirmación de contraseña no coincide.');
      return;
    }

    const payload = { ...this.user };
    delete payload.password;

    if (newPassword) {
      payload.password = newPassword;

      if (!this.isAdminView) {
        payload.currentPassword = currentPassword;
      }
    }

    this.blockUI.start();
    this.usersService
      .update(payload)
      .then(async () => {
        this.currentPassword = '';
        this.newPassword = '';
        this.confirmPassword = '';
        await this.syncAuthUserIfNeeded(this.user);
        this.blockUI.stop();
        this.snackbar.success('El usuario se ha actualizado correctamente.');
      })
      .catch((error) => {
        this.blockUI.stop();
        this.snackbar.error(error.message);
      });
  }

  fileBrowseHandler(event: Event): void {
    const files = (event.target as HTMLInputElement).files;

    if (!files?.length || !this.userId) {
      return;
    }

    const file = files[0];
    if (!file.type.startsWith('image/')) {
      this.snackbar.error('Selecciona un archivo de imagen válido.');
      return;
    }

    this.blockUI.start();
    this.apiService
      .uploadFile(`/users/upload-avatar/${this.userId}`, file)
      .then(async (updatedUser) => {
        this.user = updatedUser;
        await this.syncAuthUserIfNeeded(updatedUser);
        this.blockUI.stop();
        this.snackbar.success('Foto de perfil actualizada.');
      })
      .catch((error) => {
        this.blockUI.stop();
        this.snackbar.error(error.message || 'No se pudo subir la imagen.');
      });
  }

  async toggleNotifications(): Promise<void> {
    if (!this.notificationsSupported) {
      return;
    }

    if (this.notificationsEnabled) {
      await this.notificationsService.disable();
      this.notificationsEnabled = false;
      this.snackbar.success('Notificaciones desactivadas.');
      this.cdr.markForCheck();
      return;
    }

    const permission = this.notificationsService.getPermissionStatus();

    if (permission === 'denied') {
      this.snackbar.info(
        'Las notificaciones estan bloqueadas en el navegador. Activalas desde configuracion del sitio.',
      );
      return;
    }

    if (permission === 'default') {
      const confirmRef = this.dialog.open(ConfirmModalComponent, {
        data: {
          eyebrow: 'Permisos del navegador',
          title: 'Activar notificaciones',
          message:
            'Te avisaremos antes del cierre de ronda para que no se te pasen tus pronósticos.',
          icon: 'notifications_active',
          tone: 'highlight',
          cancelText: 'Ahora no',
          confirmText: 'Continuar',
        },
      });

      const confirmed = await firstValueFrom(confirmRef.afterClosed());

      if (!confirmed) {
        return;
      }
    }

    this.notificationsEnabled = await this.notificationsService.enable();

    if (this.notificationsEnabled) {
      this.snackbar.success(
        'Notificaciones activadas. Te avisaremos antes del cierre.',
      );
    }

    this.cdr.markForCheck();
  }

  private async syncAuthUserIfNeeded(user: AuthUser): Promise<void> {
    const currentUser = await this.authService.getUserAsync();

    if (currentUser?._id === user?._id) {
      await this.authService.updateCurrentUser(user);
    }
  }
}
